import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, GitCompareArrows } from "lucide-react";
import { SPIRITS, padNum, localizeSpirit } from "../../data/goetia";
import { useLang } from "../../config/i18n";

export const CompareTray = ({ compare, setCompare }) => {
  const [open, setOpen] = useState(false);
  const { lang, t } = useLang();
  const g = t.goetia;
  const selected = compare
    .map((slug) => SPIRITS.find((s) => s.slug === slug))
    .filter(Boolean)
    .map((s) => localizeSpirit(s, lang));

  const remove = (slug) => setCompare(compare.filter((s) => s !== slug));

  return (
    <>
      <AnimatePresence>
        {selected.length > 0 && (
          <motion.div
            data-testid="compare-tray"
            initial={{ y: 90, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 90, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[150] w-[calc(100%-2.5rem)] max-w-xl rounded-xl bg-[#16161B]/95 backdrop-blur-xl border border-[#C5A059]/40 shadow-[0_10px_50px_-10px_rgba(0,0,0,0.8)] p-4"
          >
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {selected.map((s) => (
                  <span key={s.slug} className="relative w-10 h-10 rounded-full border border-[#C5A059]/50 bg-[#0D0D10] overflow-hidden">
                    <img src={s.sigil} alt={s.name} className="w-full h-full object-contain sigil-gold p-1.5" />
                  </span>
                ))}
              </div>
              <p className="flex-1 text-xs text-[#A3A3AF] font-light">
                {selected.map((s) => s.name).join(" • ")}
                <span className="block text-[10px] text-[#6E6E7D] uppercase tracking-[0.15em] mt-0.5">
                  {selected.length} {g.compareSelected}
                </span>
              </p>
              <button
                data-testid="compare-open-btn"
                onClick={() => setOpen(true)}
                disabled={selected.length < 2}
                className="btn-gold min-h-[42px] px-5 text-[10px] disabled:opacity-40 disabled:pointer-events-none"
              >
                <GitCompareArrows size={14} /> {g.compare}
              </button>
              <button
                data-testid="compare-clear-btn"
                onClick={() => setCompare([])}
                aria-label="Limpar comparação"
                className="w-9 h-9 flex items-center justify-center text-[#6E6E7D] hover:text-[#C5A059] transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-[#0A0A0C]/95 backdrop-blur-md overflow-y-auto p-4 sm:p-8"
            onClick={() => setOpen(false)}
            data-testid="compare-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-5xl mx-auto my-8 rounded-2xl bg-[#121216] border border-[#C5A059]/30 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-[#22222B]">
                <h3 className="font-heading text-lg sm:text-xl tracking-[0.12em] uppercase text-[#F7F7F8]">
                  {g.compareTitle}
                </h3>
                <button
                  data-testid="compare-close-btn"
                  onClick={() => setOpen(false)}
                  className="w-10 h-10 flex items-center justify-center text-[#C5A059] border border-[#C5A059]/40 rounded-full"
                  aria-label="Fechar comparação"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left min-w-[640px]">
                  <thead>
                    <tr className="border-b border-[#22222B]">
                      <th className="px-6 sm:px-8 py-4 text-[10px] font-semibold tracking-[0.2em] uppercase text-[#C5A059]">{g.thSpirit}</th>
                      <th className="px-6 py-4 text-[10px] font-semibold tracking-[0.2em] uppercase text-[#C5A059]">{g.thRank}</th>
                      <th className="px-6 py-4 text-[10px] font-semibold tracking-[0.2em] uppercase text-[#C5A059]">{g.thLegions}</th>
                      <th className="px-6 py-4 text-[10px] font-semibold tracking-[0.2em] uppercase text-[#C5A059]">{g.thAttributes}</th>
                      <th className="px-6 sm:px-8 py-4 text-[10px] font-semibold tracking-[0.2em] uppercase text-[#C5A059]">{g.thSigil}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.map((s) => (
                      <tr key={s.slug} data-testid={`compare-row-${s.slug}`} className="border-b border-[#22222B] last:border-0 align-top">
                        <td className="px-6 sm:px-8 py-5">
                          <span className="font-heading text-base text-[#F7F7F8] tracking-wide">{padNum(s.n)} — {s.name}</span>
                        </td>
                        <td className="px-6 py-5 text-sm text-[#D1D1DB] font-light">{s.ranks.join(lang === "en" ? " and " : " e ")}</td>
                        <td className="px-6 py-5 text-sm text-[#D1D1DB] font-light">{s.legions}</td>
                        <td className="px-6 py-5">
                          <ul className="space-y-1.5">
                            {s.attributes.map((a) => (
                              <li key={a} className="text-xs text-[#A3A3AF] font-light">◆ {a}</li>
                            ))}
                          </ul>
                        </td>
                        <td className="px-6 sm:px-8 py-5">
                          <span className="inline-block w-14 h-14 rounded-full border border-[#C5A059]/40 bg-[#0D0D10] p-2">
                            <img src={s.sigil} alt={`Sigilo de ${s.name}`} className="w-full h-full object-contain sigil-gold" />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
